<template>
  <Teleport to="body">
    <transition name="aviso-fade">
      <div v-if="visible"
           class="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 z-[99999]"
           @click.self="cerrar">

        <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-aviso-pop flex flex-col">

          <!-- Header dinámico -->
          <div :class="[
            'px-6 py-5 flex items-center gap-3 flex-shrink-0',
            headerClass
          ]">
            <div class="p-3 bg-white/20 rounded-2xl flex-shrink-0">
              <Icon :icon="headerIcon" class="text-white text-2xl sm:text-3xl" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-base sm:text-lg font-black text-white leading-tight">
                {{ headerTitulo }}
              </h3>
              <p class="text-[11px] sm:text-xs text-white/80 font-bold mt-0.5">
                Hola <span class="font-black">{{ nombreUsuario }}</span>, {{ headerSubtitulo }}
              </p>
            </div>
            <button @click="cerrar"
                    class="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all flex-shrink-0">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>

          <!-- Cuerpo scrolleable -->
          <div class="overflow-y-auto flex-1 px-4 sm:px-6 py-4">

            <!-- CASO 1: HAY FACTURAS URGENTES -->
            <template v-if="facturasUrgentes.length > 0">
              <div class="grid grid-cols-3 gap-2 mb-4">
                <div class="bg-red-50 dark:bg-red-900/20 rounded-2xl p-3 text-center border border-red-200 dark:border-red-800">
                  <p class="text-[9px] font-black text-red-500 uppercase tracking-widest">Vencidos</p>
                  <p class="text-xl font-black text-red-600 dark:text-red-400">{{ conteos.vencidos }}</p>
                </div>
                <div class="bg-orange-50 dark:bg-orange-900/20 rounded-2xl p-3 text-center border border-orange-200 dark:border-orange-800">
                  <p class="text-[9px] font-black text-orange-500 uppercase tracking-widest">Hoy</p>
                  <p class="text-xl font-black text-orange-600 dark:text-orange-400">{{ conteos.hoy }}</p>
                </div>
                <div class="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-3 text-center border border-amber-200 dark:border-amber-800">
                  <p class="text-[9px] font-black text-amber-500 uppercase tracking-widest">Próx. 3d</p>
                  <p class="text-xl font-black text-amber-600 dark:text-amber-400">{{ conteos.tresDias }}</p>
                </div>
              </div>

              <div class="space-y-2.5">
                <div v-for="p in facturasUrgentes"
                     :key="'aviso-' + p.idPagoServicio"
                     class="flex items-start gap-3 p-3 rounded-2xl border-2 transition-all"
                     :class="getRowClass(p)">

                  <div class="w-11 h-11 flex-shrink-0 rounded-xl flex items-center justify-center shadow-sm mt-0.5"
                       :style="{ backgroundColor: getColorServicio(p) + '20' }">
                    <Icon :icon="getIconoServicio(p)"
                          class="text-xl"
                          :style="{ color: getColorServicio(p) }" />
                  </div>

                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-black text-gray-900 dark:text-white truncate">
                      {{ p.nombreServicio || p.tipoServicio || 'Servicio' }}
                    </p>
                    <p v-if="p.aliasContrato && p.aliasContrato !== p.nombreServicio"
                       class="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                      {{ p.aliasContrato }}
                    </p>
                    <div class="flex items-center gap-2 text-[10px] mt-1">
                      <span class="text-gray-500 dark:text-gray-400">
                        {{ formatearPeriodo(p.periodo) || 'Sin período' }}
                      </span>
                      <span class="text-gray-300">•</span>
                      <span :class="getDiasClass(p)" class="font-black">
                        {{ getDiasTexto(p) }}
                      </span>
                    </div>
                  </div>

                  <div class="text-right flex-shrink-0">
                    <p class="text-sm font-black text-gray-900 dark:text-white">
                      {{ formatearMoneda(p.saldo_pendiente || p.monto_total) }}
                    </p>
                    <button @click="irAlModulo"
                            class="text-[10px] font-black text-blue-600 hover:text-blue-700 mt-0.5 flex items-center gap-0.5 ml-auto">
                      Ver
                      <Icon icon="mdi:arrow-right" class="text-xs" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800 flex items-start gap-2.5">
                <Icon icon="mdi:information-outline" class="text-blue-600 text-lg flex-shrink-0 mt-0.5" />
                <p class="text-xs font-bold text-blue-700 dark:text-blue-300">
                  Por favor, revise los servicios próximos a vencer para evitar mora o suspensión.
                </p>
              </div>
            </template>

            <!-- CASO 2: NO HAY URGENTES PERO SÍ PENDIENTES -->
            <template v-else-if="facturasPendientes.length > 0">
              <div class="text-center py-4 mb-3">
                <div class="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mb-3">
                  <Icon icon="mdi:check-circle" class="text-4xl text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 class="text-base font-black text-gray-900 dark:text-white mb-1">
                  ¡Todo bajo control!
                </h3>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  No tienes servicios vencidos ni próximos a vencer.
                </p>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4">
                <div class="flex items-center gap-2 mb-3">
                  <Icon icon="mdi:calendar-clock" class="text-blue-600 text-lg" />
                  <p class="text-xs font-black text-gray-700 dark:text-gray-300 uppercase tracking-widest">
                    Tus próximos pagos ({{ facturasPendientes.length }})
                  </p>
                </div>

                <div class="space-y-2 max-h-64 overflow-y-auto">
                  <div v-for="p in facturasPendientes.slice(0, 5)"
                       :key="'prox-' + p.idPagoServicio"
                       class="flex items-center gap-3 p-2.5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">

                    <div class="w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center"
                         :style="{ backgroundColor: getColorServicio(p) + '20' }">
                      <Icon :icon="getIconoServicio(p)"
                            class="text-base"
                            :style="{ color: getColorServicio(p) }" />
                    </div>

                    <div class="flex-1 min-w-0">
                      <p class="text-xs font-black text-gray-900 dark:text-white truncate">
                        {{ p.nombreServicio || p.tipoServicio }}
                      </p>
                      <p class="text-[10px] text-gray-500 truncate">
                        {{ formatearPeriodo(p.periodo) }} • {{ getDiasTexto(p) }}
                      </p>
                    </div>

                    <p class="text-xs font-black text-gray-900 dark:text-white flex-shrink-0">
                      {{ formatearMoneda(p.saldo_pendiente || p.monto_total) }}
                    </p>
                  </div>

                  <p v-if="facturasPendientes.length > 5"
                     class="text-[10px] text-center text-gray-400 pt-1">
                    Y {{ facturasPendientes.length - 5 }} más...
                  </p>
                </div>
              </div>
            </template>

            <!-- CASO 3: NO HAY NADA -->
            <template v-else>
              <div class="text-center py-8">
                <div class="inline-flex items-center justify-center w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mb-4">
                  <Icon icon="mdi:party-popper" class="text-5xl text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 class="text-lg font-black text-gray-900 dark:text-white mb-1">
                  ¡Excelente!
                </h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
                  No tienes servicios pendientes de pago en este momento. Todo está al día. 🎉
                </p>
              </div>

              <div class="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-4 border border-blue-200 dark:border-blue-800 flex items-start gap-2.5">
                <Icon icon="mdi:lightbulb-on-outline" class="text-blue-600 text-lg flex-shrink-0 mt-0.5" />
                <p class="text-xs font-bold text-blue-700 dark:text-blue-300">
                  Recuerda que puedes agregar nuevos contratos de servicios y generar sus facturas desde el módulo de Administración.
                </p>
              </div>
            </template>

          </div>

          <!-- Footer -->
          <div class="px-4 sm:px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <button @click="cerrar"
                    class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-2xl font-black text-xs tracking-widest hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
              Cerrar
            </button>
            <button @click="irAlModulo"
                    class="flex-[2] py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2">
              <Icon icon="mdi:receipt-text" class="text-base" />
              Ir a Pagos de Servicios
            </button>
          </div>

        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { Icon } from '@iconify/vue';
import { obtenerPagos, formatearMoneda, formatearPeriodo } from '@/api/pagoServicio.js';
import { getColorServicio, getIconoServicio } from '@/api/servicioPublico.js';

// ============================================================
// 🛡️ FLAG GLOBAL A NIVEL DE MÓDULO
// ============================================================
let GLOBAL_AVISO_MOSTRADO = false;
let GLOBAL_AVISO_EN_PROCESO = false;

export default {
  name: 'AvisoServiciosPublicos',
  components: { Icon },

  data() {
    return {
      visible: false,
      facturasUrgentes: [],
      facturasPendientes: [],
      verificando: false,
      intervalId: null,
      rapidIntervalId: null,
      intentos: 0,
      instanciaId: Math.random().toString(36).substring(2, 8),
    };
  },

  computed: {
    // ============================================================
    // 🔧 FIX: Acepta múltiples variantes de nombres de campo
    // Tu objeto user guarda: { roleId, role, name }
    // Pero también puede venir: { idRol, nombreRol, nombre }
    // ============================================================
    usuarioActual() {
      const keys = ['user', 'user_data', 'usuario', 'currentUser'];
      for (const k of keys) {
        try {
          const raw = localStorage.getItem(k);
          if (raw) {
            const parsed = JSON.parse(raw);
            // Detectar cualquiera de las variantes posibles
            const tieneRol = parsed && (
              parsed.idRol !== undefined ||
              parsed.id_rol !== undefined ||
              parsed.roleId !== undefined ||       // 👈 tu caso
              parsed.rol !== undefined ||
              parsed.role !== undefined ||          // 👈 tu caso
              parsed.nombreRol !== undefined
            );
            if (tieneRol) {
              console.log(`✅ [${this.instanciaId}] Usuario encontrado en "${k}":`, parsed);
              return parsed;
            }
          }
        } catch (e) {
          console.warn(`Error parseando ${k}:`, e);
        }
      }
      console.log(`⚠️ [${this.instanciaId}] No se encontró usuario en localStorage`);
      return null;
    },

    nombreUsuario() {
      const u = this.usuarioActual;
      if (!u) return 'Usuario';
      return u.nombre || u.nombreUsuario || u.username || u.name || 'Usuario';
    },

    // ============================================================
    // 🎯 SOLO ADMIN Y SUPERADMIN (IDs 1 y 2)
    // Acepta variantes: idRol, id_rol, roleId
    // ============================================================
    esAdmin() {
      const u = this.usuarioActual;
      if (!u) {
        console.log(`⛔ [${this.instanciaId}] esAdmin: no hay usuario`);
        return false;
      }

      // ✅ Aceptar múltiples variantes
      const rolId = parseInt(
        u.idRol ?? 
        u.id_rol ?? 
        u.roleId ??     // 👈 tu caso
        0
      );
      
      const rolNombre = (
        u.nombreRol || 
        u.rol || 
        u.rolNombre || 
        u.role ||       // 👈 tu caso
        ''
      ).toLowerCase().trim();

      const esAdminPorId = rolId === 1 || rolId === 2;

      console.log(`🔍 [${this.instanciaId}] esAdmin check:`, { 
        rolId, 
        rolNombre, 
        esAdminPorId
      });

      return esAdminPorId;
    },

    conteos() {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      let vencidos = 0, hoy_ = 0, tresDias = 0;

      this.facturasUrgentes.forEach(p => {
        const venc = new Date(p.fecha_vencimiento);
        venc.setHours(0, 0, 0, 0);
        const diff = Math.ceil((venc - hoy) / (1000 * 60 * 60 * 24));
        if (diff < 0) vencidos++;
        else if (diff === 0) hoy_++;
        else if (diff <= 3) tresDias++;
      });

      return { vencidos, hoy: hoy_, tresDias };
    },

    headerClass() {
      if (this.conteos.vencidos > 0) return 'bg-gradient-to-r from-red-600 to-rose-600';
      if (this.conteos.hoy > 0) return 'bg-gradient-to-r from-orange-600 to-amber-600';
      if (this.facturasUrgentes.length > 0) return 'bg-gradient-to-r from-amber-500 to-yellow-600';
      if (this.facturasPendientes.length > 0) return 'bg-gradient-to-r from-blue-600 to-indigo-600';
      return 'bg-gradient-to-r from-emerald-600 to-teal-600';
    },

    headerIcon() {
      if (this.conteos.vencidos > 0) return 'mdi:alert-circle';
      if (this.conteos.hoy > 0) return 'mdi:clock-alert';
      if (this.facturasUrgentes.length > 0) return 'mdi:bell-ring';
      if (this.facturasPendientes.length > 0) return 'mdi:calendar-check';
      return 'mdi:check-decagram';
    },

    headerTitulo() {
      if (this.conteos.vencidos > 0) return '⚠️ Servicios Vencidos';
      if (this.conteos.hoy > 0) return '🔔 Servicios Vencen Hoy';
      if (this.facturasUrgentes.length > 0) return '📅 Próximos Vencimientos';
      if (this.facturasPendientes.length > 0) return '✅ Todo Bajo Control';
      return '🎉 ¡Todo al Día!';
    },

    headerSubtitulo() {
      if (this.facturasUrgentes.length > 0) {
        return `tienes ${this.facturasUrgentes.length} servicio(s) por atender pronto`;
      }
      if (this.facturasPendientes.length > 0) {
        return `tienes ${this.facturasPendientes.length} servicio(s) pendiente(s) pero sin urgencia`;
      }
      return 'no tienes servicios pendientes';
    }
  },

  mounted() {
    console.log(`🚀 AvisoServiciosPublicos montado [${this.instanciaId}]`);

    if (GLOBAL_AVISO_MOSTRADO) {
      console.log(`⛔ [${this.instanciaId}] Ya se mostró por otra instancia`);
      return;
    }

    // Intentar de inmediato
    this.intentarVerificar();

    // ⚡ Reintentos rápidos cada 1s (para cuando el user tarde en cargar)
    let rapidos = 0;
    this.rapidIntervalId = setInterval(() => {
      rapidos++;
      if (GLOBAL_AVISO_MOSTRADO || this.visible) {
        clearInterval(this.rapidIntervalId);
        return;
      }
      if (rapidos > 5) {
        clearInterval(this.rapidIntervalId);
        return;
      }
      console.log(`⚡ [${this.instanciaId}] intento rápido ${rapidos}`);
      this.intentarVerificar();
    }, 1000);

    // 🔄 Reintentos lentos cada 2.5s
    this.intervalId = setInterval(() => {
      this.intentos++;
      if (GLOBAL_AVISO_MOSTRADO) {
        clearInterval(this.intervalId);
        return;
      }
      if (this.intentos > 10 || this.visible) {
        clearInterval(this.intervalId);
        return;
      }
      console.log(`🔄 [${this.instanciaId}] reintento ${this.intentos}`);
      this.intentarVerificar();
    }, 2500);

    window.addEventListener('userLoggedIn', this.onUserLoggedIn);
    window.addEventListener('userDataLoaded', this.onUserDataLoaded);
    window.addEventListener('storage', this.onStorageChange);
  },

  beforeDestroy() {
    if (this.intervalId) clearInterval(this.intervalId);
    if (this.rapidIntervalId) clearInterval(this.rapidIntervalId);
    window.removeEventListener('userLoggedIn', this.onUserLoggedIn);
    window.removeEventListener('userDataLoaded', this.onUserDataLoaded);
    window.removeEventListener('storage', this.onStorageChange);
  },

  methods: {
    formatearMoneda,
    formatearPeriodo,
    getColorServicio,
    getIconoServicio,

    get yaMostrado() {
      return sessionStorage.getItem('avisoServiciosMostrado') === 'true';
    },

    onUserLoggedIn() {
      console.log(`📣 [${this.instanciaId}] Evento userLoggedIn`);
      if (GLOBAL_AVISO_MOSTRADO) return;
      // Intentar varias veces después del evento (por si el user tarda)
      setTimeout(() => this.intentarVerificar(), 300);
      setTimeout(() => this.intentarVerificar(), 800);
      setTimeout(() => this.intentarVerificar(), 1500);
    },

    onUserDataLoaded() {
      console.log(`📣 [${this.instanciaId}] Evento userDataLoaded`);
      if (GLOBAL_AVISO_MOSTRADO) return;
      setTimeout(() => this.intentarVerificar(), 500);
      setTimeout(() => this.intentarVerificar(), 1200);
    },

    onStorageChange(e) {
      if (e.key === 'user' || e.key === 'user_data') {
        if (GLOBAL_AVISO_MOSTRADO) return;
        this.intentarVerificar();
      }
    },

    async intentarVerificar() {
      const u = this.usuarioActual;
      
      console.log(`🔍 [${this.instanciaId}] intentarVerificar`, {
        yaMostrado: this.yaMostrado,
        verificando: this.verificando,
        visible: this.visible,
        esAdmin: this.esAdmin,
        GLOBAL_MOSTRADO: GLOBAL_AVISO_MOSTRADO,
        GLOBAL_EN_PROCESO: GLOBAL_AVISO_EN_PROCESO,
        usuarioEncontrado: !!u,
        rolId: u ? (u.idRol ?? u.id_rol ?? u.roleId) : 'N/A',
      });

      // 🛡️ GUARD GLOBAL
      if (GLOBAL_AVISO_MOSTRADO) {
        console.log(`⛔ [${this.instanciaId}] GLOBAL: ya mostrado, abortando`);
        if (this.intervalId) clearInterval(this.intervalId);
        if (this.rapidIntervalId) clearInterval(this.rapidIntervalId);
        return;
      }

      if (GLOBAL_AVISO_EN_PROCESO) {
        console.log(`⏳ [${this.instanciaId}] GLOBAL: otra instancia procesando, esperando`);
        return;
      }

      if (this.yaMostrado) {
        console.log(`ℹ️ [${this.instanciaId}] Ya se mostró en esta sesión`);
        if (this.intervalId) clearInterval(this.intervalId);
        if (this.rapidIntervalId) clearInterval(this.rapidIntervalId);
        return;
      }

      if (this.verificando) return;
      if (this.visible) return;

      // 🛡️ Si no es admin, no hacer nada (pero el retry sigue)
      if (!this.esAdmin) {
        console.log(`⛔ [${this.instanciaId}] No es admin (aún), esperando siguiente intento...`);
        return;
      }

      // 🛡️ Marcar como "en proceso" a nivel global
      GLOBAL_AVISO_EN_PROCESO = true;
      this.verificando = true;

      try {
        const res = await obtenerPagos();
        if (!res.success) {
          console.warn(`⚠️ [${this.instanciaId}] res.success = false`);
          return;
        }

        const pagos = res.data || [];
        console.log(`📊 [${this.instanciaId}] Total pagos: ${pagos.length}`);

        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        const pendientes = pagos.filter(p =>
          p.estado === 'pendiente' || p.estado === 'parcial'
        );

        const urgentes = pendientes.filter(p => {
          const venc = new Date(p.fecha_vencimiento);
          venc.setHours(0, 0, 0, 0);
          const diff = Math.ceil((venc - hoy) / (1000 * 60 * 60 * 24));
          return diff <= 3;
        }).sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento));

        const pendientesNoUrgentes = pendientes.filter(p => {
          const venc = new Date(p.fecha_vencimiento);
          venc.setHours(0, 0, 0, 0);
          const diff = Math.ceil((venc - hoy) / (1000 * 60 * 60 * 24));
          return diff > 3;
        }).sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento));

        console.log(`🚨 [${this.instanciaId}] Urgentes: ${urgentes.length}, Pendientes: ${pendientesNoUrgentes.length}`);

        this.facturasUrgentes = urgentes.slice(0, 8);
        this.facturasPendientes = pendientesNoUrgentes.slice(0, 10);

        // 🛡️ Última validación antes de mostrar
        if (!this.esAdmin || GLOBAL_AVISO_MOSTRADO) {
          console.log(`⛔ [${this.instanciaId}] Cancelado antes de mostrar`);
          return;
        }

        setTimeout(() => {
          if (GLOBAL_AVISO_MOSTRADO) {
            console.log(`⛔ [${this.instanciaId}] Otra instancia mostró primero`);
            return;
          }

          if (this.esAdmin) {
            GLOBAL_AVISO_MOSTRADO = true;
            this.visible = true;
            console.log(`✅ [${this.instanciaId}] Modal mostrado`);
            if (this.intervalId) clearInterval(this.intervalId);
            if (this.rapidIntervalId) clearInterval(this.rapidIntervalId);
          }
        }, 400);

      } catch (e) {
        console.error(`❌ [${this.instanciaId}] Error:`, e);
      } finally {
        this.verificando = false;
        GLOBAL_AVISO_EN_PROCESO = false;
      }
    },

    cerrar() {
      this.visible = false;
      GLOBAL_AVISO_MOSTRADO = true;
      sessionStorage.setItem('avisoServiciosMostrado', 'true');
      if (this.intervalId) clearInterval(this.intervalId);
      if (this.rapidIntervalId) clearInterval(this.rapidIntervalId);
    },

    irAlModulo() {
      this.cerrar();
      this.$router.push('/pago-servicio-publico');
    },

    getDias(p) {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const venc = new Date(p.fecha_vencimiento);
      venc.setHours(0, 0, 0, 0);
      return Math.ceil((venc - hoy) / (1000 * 60 * 60 * 24));
    },

    getDiasTexto(p) {
      const d = this.getDias(p);
      if (d < 0) return `Vencido hace ${Math.abs(d)}d`;
      if (d === 0) return '¡Vence HOY!';
      if (d === 1) return 'Vence mañana';
      return `En ${d} día(s)`;
    },

    getDiasClass(p) {
      const d = this.getDias(p);
      if (d < 0) return 'text-red-600 dark:text-red-400';
      if (d === 0) return 'text-orange-600 dark:text-orange-400';
      return 'text-amber-600 dark:text-amber-400';
    },

    getRowClass(p) {
      const d = this.getDias(p);
      if (d < 0) return 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800';
      if (d === 0) return 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-800';
      return 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800';
    }
  }
};
</script>

<style scoped>
.aviso-fade-enter-active,
.aviso-fade-leave-active {
  transition: opacity 0.3s ease;
}

.aviso-fade-enter-from,
.aviso-fade-leave-to {
  opacity: 0;
}

@keyframes avisoPop {
  0% { opacity: 0; transform: scale(0.9) translateY(20px); }
  60% { opacity: 1; transform: scale(1.02) translateY(-2px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

.animate-aviso-pop {
  animation: avisoPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
</style>